import { AnimatePresence, motion } from "motion/react";
import { LogOut, MapPin, Menu as MenuIcon, Search, X } from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { visibleNav } from "../domain/access";
import { ROLE_LABEL } from "../domain/status";
import { cn } from "../lib/cn";
import { ThemeSwitch } from "../ui/components/ThemeSwitch";
import { Avatar } from "../ui/components/Avatar";
import { Kbd } from "../ui/components/Kbd";
import { useOpenPalette } from "./AppFrame";
import { BrandTile, useBusinessNames } from "./BrandMark";
import { isMac } from "./CommandPalette";
import { NAV_ICON } from "./navIcons";
import { OnlinePill, SyncButton, TillPill } from "./StatusPills";

/** Back-office shell (§6.1): left sidebar, glass top bar with the status pills, and a content
 * area that scrolls on its own. ≥1280 px full sidebar; 1024–1279 an icon rail; below 1024 the
 * sidebar becomes an off-canvas drawer behind a menu button. */
export function BackOfficeShell() {
  const [drawer, setDrawer] = useState(false);
  const loc = useLocation();
  // Close the mobile drawer when the route changes (adjusting state during render, not in an effect).
  const [path, setPath] = useState(loc.pathname);
  if (path !== loc.pathname) {
    setPath(loc.pathname);
    setDrawer(false);
  }

  return (
    <div className="flex min-h-0 flex-1">
      <aside className="no-print hidden shrink-0 border-r border-line bg-surface lg:flex lg:w-[76px] xl:w-[248px]">
        <Sidebar collapsedBelowXl />
      </aside>

      <AnimatePresence>
        {drawer && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <motion.div
              className="absolute inset-0 bg-scrim backdrop-blur-[1px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawer(false)}
              aria-hidden="true"
            />
            <motion.aside
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
              className="absolute inset-y-0 left-0 flex w-[280px] max-w-[86vw] border-r border-line bg-surface shadow-[var(--shadow-raised)]"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 460, damping: 40 }}
            >
              <button
                type="button"
                onClick={() => setDrawer(false)}
                className="btn btn-ghost btn-icon btn-sm absolute right-3 top-4 z-10"
                aria-label="Close navigation"
              >
                <X />
              </button>
              <Sidebar />
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenu={() => setDrawer(true)} />
        <main
          id="main"
          className="relative flex min-h-0 flex-1 flex-col overflow-y-auto p-4 sm:p-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Sidebar({ collapsedBelowXl }: { collapsedBelowXl?: boolean }) {
  const { user, logout } = useAuth();
  const { business, branch } = useBusinessNames();
  const items = visibleNav(user);
  // Hide text in the 1024–1279 rail; full labels from xl and in the mobile drawer.
  // not-sr-only resets position to static, which would drop the label under the active
  // highlight layer; keep it positioned at xl.
  const label = collapsedBelowXl
    ? "lg:sr-only xl:not-sr-only xl:!relative"
    : "";
  return (
    <nav aria-label="Main" className="flex w-full min-h-0 flex-col">
      <div
        className={cn(
          "flex h-[68px] shrink-0 items-center gap-3 border-b border-line px-4",
          collapsedBelowXl &&
            "lg:justify-center lg:px-0 xl:justify-start xl:px-4",
        )}
      >
        <span className="relative shrink-0">
          <BrandTile size={38} />
          <span
            className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-success ring-2 ring-[var(--surface)]"
            aria-hidden="true"
          />
        </span>
        <div
          className={cn(
            "min-w-0 pr-8 lg:pr-0",
            collapsedBelowXl && "lg:hidden xl:block",
          )}
        >
          <div
            className="truncate text-md font-extrabold leading-tight tracking-[-0.01em] text-neutral-900"
            title={business}
          >
            {business}
          </div>
          {branch && (
            <div className="mt-0.5 flex items-center gap-1 truncate text-xs font-medium text-neutral-500">
              <MapPin
                className="h-3 w-3 shrink-0 text-neutral-400"
                aria-hidden="true"
              />
              <span className="truncate">{branch}</span>
            </div>
          )}
        </div>
      </div>

      <div
        className={cn(
          "px-6 pb-1.5 pt-4 text-2xs font-bold uppercase tracking-[0.12em] text-neutral-500",
          collapsedBelowXl && "lg:hidden xl:block",
        )}
        aria-hidden="true"
      >
        Workspace
      </div>
      <ul
        className={cn(
          "min-h-0 flex-1 space-y-1 overflow-y-auto px-3 pb-3",
          collapsedBelowXl && "lg:px-3 lg:pt-3 xl:px-3 xl:pt-0",
        )}
      >
        {items.map((i) => (
          <li key={i.key}>
            <NavLink
              to={i.path}
              end={i.path === "/"}
              title={collapsedBelowXl ? i.label : undefined}
              className={({ isActive }) =>
                cn(
                  "group relative flex h-10 items-center gap-3 rounded-[10px] px-3 text-base font-semibold outline-offset-2 transition-colors duration-150 [&_svg]:h-[18px] [&_svg]:w-[18px] [&_svg]:shrink-0",
                  collapsedBelowXl &&
                    "lg:h-11 lg:justify-center lg:px-0 xl:h-10 xl:justify-start xl:px-3",
                  isActive
                    ? "text-primary-text"
                    : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId={
                        collapsedBelowXl ? "nav-active" : "nav-active-drawer"
                      }
                      className="absolute inset-0 rounded-[10px] bg-primary-tint ring-1 ring-inset ring-primary/15"
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 40,
                      }}
                    />
                  )}
                  {isActive && (
                    <span
                      className="absolute -left-3 top-2 bottom-2 w-[3px] rounded-r-full bg-primary shadow-[0_0_12px_var(--brand-ring)]"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className={cn(
                      "relative transition-colors",
                      isActive
                        ? "text-primary-text"
                        : "text-neutral-400 group-hover:text-neutral-700",
                    )}
                  >
                    {NAV_ICON[i.key]}
                  </span>
                  <span className={cn("relative truncate", label)}>
                    {i.label}
                  </span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>

      <div className="shrink-0 border-t border-line p-3">
        <ThemeSwitch
          className={cn(
            "mb-3 flex w-full [&>button]:flex-1",
            collapsedBelowXl &&
              "lg:flex-col lg:gap-0.5 xl:flex-row xl:gap-0 lg:[&_span]:sr-only xl:[&_span]:not-sr-only",
          )}
        />
        {user && (
          <div
            className={cn(
              "flex items-center gap-3 rounded-[12px] border border-line bg-neutral-50 p-2.5",
              collapsedBelowXl &&
                "lg:flex-col lg:gap-2 lg:border-0 lg:bg-transparent lg:p-0 xl:flex-row xl:gap-3 xl:border xl:bg-neutral-50 xl:p-2.5",
            )}
          >
            <Avatar
              name={user.fullName}
              size={34}
              className="!ring-[var(--surface)]"
            />
            <div
              className={cn(
                "min-w-0 flex-1",
                collapsedBelowXl && "lg:hidden xl:block",
              )}
            >
              <div
                className="truncate text-sm font-bold text-neutral-900"
                title={user.fullName}
              >
                {user.fullName}
              </div>
              <div className="mt-0.5 truncate text-2xs font-bold uppercase tracking-[0.08em] text-primary-text">
                {ROLE_LABEL[user.role]}
              </div>
            </div>
            <button
              type="button"
              onClick={logout}
              className="btn btn-ghost btn-icon btn-sm text-neutral-500 hover:text-danger-ink"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut />
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const openPalette = useOpenPalette();
  return (
    <header className="glass no-print sticky top-0 z-30 flex h-[68px] shrink-0 items-center gap-3 px-4 sm:px-6">
      <button
        type="button"
        onClick={onMenu}
        className="btn btn-ghost btn-icon btn-sm lg:hidden"
        aria-label="Open navigation"
      >
        <MenuIcon />
      </button>
      <button
        type="button"
        onClick={openPalette}
        className="group flex h-10 w-10 shrink-0 items-center justify-center gap-2.5 rounded-[12px] sm:w-auto sm:min-w-0 sm:max-w-[380px] sm:flex-1 sm:shrink sm:justify-start border border-line bg-[var(--field)] px-3 text-left text-sm text-neutral-500 shadow-[var(--shadow-card)] transition-colors duration-150 hover:border-neutral-300 hover:text-neutral-700"
        aria-label="Search pages and actions"
      >
        <Search
          className="h-4 w-4 shrink-0 text-neutral-400 group-hover:text-neutral-500"
          aria-hidden="true"
        />
        <span className="hidden flex-1 truncate sm:inline">Jump to…</span>
        <Kbd className="hidden sm:inline-flex">{isMac() ? "⌘K" : "Ctrl K"}</Kbd>
      </button>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {/* Below 640 px the till and online pills drop their words (names stay the same). */}
        <span className="contents sm:hidden">
          <TillPill compact />
        </span>
        <span className="hidden sm:contents">
          <TillPill />
        </span>
        <SyncButton />
        <span className="contents sm:hidden">
          <OnlinePill compact />
        </span>
        <span className="hidden sm:contents">
          <OnlinePill />
        </span>
      </div>
    </header>
  );
}
